import { useEffect, useState } from "react"
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../@/components/ui/select";
import { api_endpoint } from "@/constants/constant"
import axios from "axios"
import { useAuth } from "@clerk/clerk-react"
import { Spinner } from "@/components/spinner"
import { toast } from "sonner"
import Papa from "papaparse";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";

export default function BulkEnrollStudents({
    cohortId,
    initialCohortId,
    cohorts = [],
    onEnrolled,
    open,
    onOpenChange,
    enrolledStudentIds = [],
    showTrigger = true,
}) {
    // const [open, setOpen] = useState(false)
    const [allStudents, setAllStudents] = useState([])
    const [allCourses, setAllCourses] = useState([])
    const [allInstructors, setAllInstructors] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [selectedCourse, setSelectedCourse] = useState("")
    const [selectedInstructor, setSelectedInstructor] = useState("")
    const [selectedCohort, setSelectedCohort] = useState("")
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [cohortStudentIds, setCohortStudentIds] = useState([]);
    const { getToken } = useAuth()


    const fetchAllStudents = async (token) => {
        try {
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }
            const users = await fetchAllPaginatedItems({
                url: api_endpoint + `/user/users`,
                token,
                selectItems: (data) => data?.users ?? [],
                mode: "skip",
                pageSize: 200,
                headers: reqConfig.headers,
            })
            setAllStudents(users || [])
        } catch (error) {
            console.error("Error fetching students:", error)
            toast.error("Error fetching students")
            setAllStudents([])
        }
    }

    const fetchAllInstructors = async (token) => {
        try {
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }
            const instructors = await fetchAllPaginatedItems({
                url: api_endpoint + `/user/users/role/${"Instructor"}`,
                token,
                selectItems: (data) => data?.users ?? [],
                mode: "skip",
                pageSize: 200,
                headers: reqConfig.headers,
            })
            setAllInstructors(instructors || [])
        } catch (error) {
            console.error("Error fetching instructors:", error)
            toast.error("Error fetching instructors")
            setAllInstructors([])
        }
    }

    useEffect(() => {
        if (open) {
            setLoading(true)
            getToken().then(token => {
                Promise.all([
                    fetchAllStudents(token),
                    fetchAllInstructors(token)
                ]).finally(() => setLoading(false))
            })
        }
    }, [open, getToken])


    useEffect(() => {
        if (open) {
            async function fetchAllCourses() {
                try {
                    setLoading(true)

                    // Fetch all courses no token required
                    const allResponse = await axios.get(api_endpoint + "/course/")
                    // console.log("All courses fetched:", allResponse.data.courses)
                    setAllCourses(allResponse.data.courses || [])

                } catch (error) {
                    console.error("Failed to fetch courses:", error)
                    toast.error("Failed to fetch courses")
                    setAllCourses([])

                } finally {
                    setLoading(false)
                }
            }
            fetchAllCourses()
        }
    }, [open])

    // Prefill cohort when opened from a cohort-group context.
    useEffect(() => {
        if (!open) return;
        const prefill = initialCohortId ?? cohortId;
        if (prefill == null || String(prefill).trim() === "") return;
        setSelectedCohort(String(prefill));
    }, [open, initialCohortId, cohortId]);

    useEffect(() => {
        if (open && selectedCohort) {
            async function fetchCohortEnrolments() {
                setLoading(true);
                try {
                    const token = await getToken();
                    const reqConfig = {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    };
                    const enrolled = await fetchAllPaginatedItems({
                        url: api_endpoint + `/cohort/enrolments/${selectedCohort}`,
                        token,
                        headers: reqConfig.headers,
                        mode: "skip",
                        pageSize: 200,
                        maxPages: 200,
                        dedupe: false,
                        selectItems: (data) => data?.enrolments ?? [],
                    });
                    setCohortStudentIds(enrolled.map(e => e.user_id));
                } catch (error) {
                    setCohortStudentIds([]);
                } finally {
                    setLoading(false);
                }
            }
            fetchCohortEnrolments();
        }
    }, [open, selectedCohort, getToken]);

    const handleCsvUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
                const users = results.data;
                const emails = users.map(u =>
                    (u.email || u.Email || u.EMAIL || u.emails || u.Emails)?.trim().toLowerCase()
                ).filter(Boolean);

                // Only consider students currently visible in the filtered list
                const visibleEmails = studentsToShow.map(s => s.email?.toLowerCase());
                const matchedIds = studentsToShow
                    .filter(student => emails.includes(student.email?.toLowerCase()))
                    .map(student => student.id);

                // Emails not found in the visible list
                const notFound = emails.filter(email => !visibleEmails.includes(email));
                // Emails already enrolled (not in studentsToShow but in allStudents)
                const allEmails = allStudents.map(s => s.email?.toLowerCase());
                const alreadyEnrolled = emails.filter(email =>
                    allEmails.includes(email) && !visibleEmails.includes(email)
                );

                setSelectedIds(matchedIds);

                let message = `${matchedIds.length} students selected from CSV.`;
                if (notFound.length > 0) message += ` ${notFound.length} emails not found.`;
                if (alreadyEnrolled.length > 0) message += ` ${alreadyEnrolled.length} emails already enrolled.`;

                toast.success(message);
            },
            error: function (error) {
                toast.error("Failed to parse CSV");
                console.error(error);
            }
        });
    };

    // Filtering logic for students to show
    let studentsToShow = allStudents;

    if (selectedCohort && cohortStudentIds.length > 0) {
        studentsToShow = studentsToShow.filter(student =>
            !cohortStudentIds.includes(student.id)
        );
    }

    

    // Apply search filter
    const filteredStudents = studentsToShow.filter(student => {
        const name = `${student.first_name ?? ""} ${student.last_name ?? ""}`.toLowerCase();
        const email = (student.email ?? "").toLowerCase();
        const term = searchTerm.toLowerCase();
        return name.includes(term) || email.includes(term);
    });


    const handleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
        )
    }

    const handleEnroll = async () => {
        setLoading(true)
        try {
            const token = await getToken()
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }
            await axios.post(api_endpoint + "/cohort/batch-enroll", {
                user_ids: selectedIds,
                cohort_id: Number(selectedCohort),
                course_id: Number(selectedCourse),
                tm_id: selectedInstructor
            }, reqConfig)

            if (onEnrolled) onEnrolled()
            if (onOpenChange) onOpenChange(false)
            // setOpen(false)
            toast.success("Students enrolled successfully")
            setSelectedIds([])
            setSelectedCourse("")
            setSelectedInstructor("")
        } catch (error) {
            console.error("Error enrolling students:", error)
            toast.error("Error enrolling students")
        } finally {
            setLoading(false)
        }
    }

    const canSubmit = selectedIds.length > 0 && selectedCourse && selectedInstructor && selectedCohort

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {showTrigger && (
                <DialogTrigger asChild>
                    <Button
                        variant=""
                        className="w-full sm:w-auto rounded-sm"
                    >
                        Enroll Students
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="w-full border border-(--neutral-300) text-(--neutral-900) font-body shadow-2xl">
                <DialogHeader className="border-(--neutral-300) ">
                    <DialogTitle className="font-heading text-2xl text-(--neutral-900)">Enroll students in a cohort</DialogTitle>
                    <DialogDescription className="text-sm text-(--neutral-600)">
                        Choose a cohort, course, and instructor, then search to select students.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap gap-2 mb-3">
                    <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                        <SelectTrigger className="min-w-35 flex-1 w-full overflow-hidden **:data-[slot=select-value]:truncate">
                            <SelectValue placeholder="Select Cohort" className="font-body" />
                        </SelectTrigger>
                        <SelectContent>
                            {cohorts.map(cohort => (
                                <SelectItem key={cohort.id} value={String(cohort.id)} className="font-body">
                                    {cohort.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                        <SelectTrigger className="min-w-35 flex-1 w-full overflow-hidden **:data-[slot=select-value]:truncate">
                            <SelectValue placeholder="Select Course" className="font-body" />
                        </SelectTrigger>
                        <SelectContent>
                            {allCourses.map(course => (
                                <SelectItem key={course.id} value={String(course.id)} className="font-body">
                                    {course.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
                        <SelectTrigger className="min-w-35 flex-1 w-full overflow-hidden **:data-[slot=select-value]:truncate">
                            <SelectValue placeholder="Select Instructor" />
                        </SelectTrigger>
                        <SelectContent>
                            {allInstructors.map(instr => (
                                <SelectItem key={instr.id} value={instr.id} className="font-body">
                                    {instr.first_name} {instr.last_name}
                                    {/* ({instr.email}) */}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="mb-2">
                    <Input
                        placeholder="Search students"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full font-body"
                    />
                </div>
                {/* <div className="mb-2">
                    <Input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="w-full font-body mb-2"
                    />
                    <div className="text-xs text-(--neutral-500) mb-2">
                        Upload a CSV file with a column named <b>email</b> to bulk select students for enrollment.
                    </div>
                </div> */}
                {loading ? (
                    <Spinner />
                ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2">
                        {filteredStudents.length === 0 ? (
                            <div className="text-sm text-(--neutral-500) font-body">
                                {searchTerm
                                    ? "Student not found"
                                    : "No students available for enrollment"}
                            </div>
                        ) : (
                            filteredStudents.map(student => (
                                <div key={student.id} className="flex items-center gap-2 border-b border-(--neutral-300) py-2">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(student.id)}
                                        onChange={() => handleSelect(student.id)}
                                        className="w-4 h-4"
                                    />
                                    <span className="font-body">{student.first_name} {student.last_name} ({student.email})</span>
                                </div>
                            ))
                        )}
                    </div>
                )}
                <DialogFooter className="sticky bottom-0 bg-(--surface-base) pt-4 border-(--neutral-300)">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={loading}
                        className=""
                    >
                        Cancel
                    </Button>
                    <Button
                        variant=""
                        onClick={handleEnroll}
                        disabled={!canSubmit || loading}
                        className=""
                    >
                        Enroll Selected
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )

}
